import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  LogOut, Users, BarChart3, DollarSign, Calendar,
  CheckCircle, XCircle, Clock, AlertCircle, Shield,
  Award, TrendingUp, Wallet, Gift
} from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // All API calls use httpOnly cookies (withCredentials: true)
  const axiosConfig = { withCredentials: true };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, usersRes, campaignsRes] = await Promise.all([
          axios.get(`${API}/admin/analytics`, axiosConfig),
          axios.get(`${API}/admin/users`, axiosConfig),
          axios.get(`${API}/admin/campaigns`, axiosConfig)
        ]);
        setAnalytics(analyticsRes.data);
        setUsers(usersRes.data.users);
        setCampaigns(campaignsRes.data.campaigns);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 403) {
          setNotification({ type: "error", message: "Admin access required" });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleUpdateCampaignStatus = async (campaignId, status) => {
    setActionLoading(campaignId);
    try {
      await axios.put(
        `${API}/admin/campaigns/${campaignId}/status`,
        { status },
        axiosConfig
      );
      setCampaigns(prev => prev.map(c => 
        c.campaign_id === campaignId ? { ...c, status } : c
      ));
      setNotification({ type: "success", message: `Campaign ${status}` });
    } catch (error) {
      setNotification({ type: "error", message: "Failed to update status" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleModerateCampaign = async (campaignId, action, reason = null) => {
    setActionLoading(campaignId);
    try {
      await axios.put(
        `${API}/admin/campaigns/${campaignId}/moderate`,
        { action, reason },
        axiosConfig
      );
      setCampaigns(prev => prev.map(c => 
        c.campaign_id === campaignId ? { ...c, moderation_status: action, status: action === 'approved' ? 'active' : c.status } : c
      ));
      setNotification({ type: "success", message: `Campaign ${action}` });
    } catch (error) {
      setNotification({ type: "error", message: "Failed to moderate campaign" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyPost = async (campaignId, creatorId) => {
    setActionLoading(`${campaignId}-${creatorId}`);
    try {
      await axios.put(
        `${API}/admin/campaigns/${campaignId}/verify-post`,
        { creator_id: creatorId, verified: true },
        { withCredentials: true }
      );
      setNotification({ type: "success", message: "Post verified and reward allocated!" });
    } catch (error) {
      setNotification({ type: "error", message: "Failed to verify post" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDistributeRewards = async () => {
    setActionLoading("distribute");
    try {
      await axios.post(`${API}/admin/distribute-rewards`, {}, { withCredentials: true });
      setNotification({ type: "success", message: "Monthly reward distribution started!" });
    } catch (error) {
      setNotification({ type: "error", message: "Failed to start distribution" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const pendingModeration = campaigns.filter(c => c.moderation_status === "pending");

  return (
    <div className="min-h-screen bg-[#05060D]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-10 h-10"
            />
            <div>
              <span className="text-lg font-bold text-white">EleKtro Admin</span>
              <span className="text-[10px] text-gray-500 block">Conscious Creator</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right mr-4">
              <div className="text-sm text-gray-400">Admin</div>
              <div className="font-semibold text-white">{user?.name}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            notification.type === "success" ? "bg-green-500/10 border border-green-500/30 text-green-400" :
            "bg-red-500/10 border border-red-500/30 text-red-400"
          }`}>
            <AlertCircle className="w-5 h-5" />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto">×</button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold font-['Unbounded'] text-white mb-2">
              Admin Dashboard
            </h1>
            <p className="text-gray-400">Platform overview and management</p>
          </div>
          
          {/* Pending Moderation Alert */}
          {pendingModeration.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
              <Shield className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 font-medium">{pendingModeration.length} pending review</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 p-1 bg-white/5 rounded-xl inline-flex">
          {["overview", "moderation", "users", "campaigns", "rewards"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
              className={`px-6 py-3 rounded-lg font-semibold transition-all capitalize ${
                activeTab === tab ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              {tab}
              {tab === "moderation" && pendingModeration.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
                  {pendingModeration.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && analytics && (
          <div className="space-y-6">
            {/* User Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Users", value: analytics.users.total, icon: Users, color: "cyan" },
                { label: "Businesses", value: analytics.users.businesses, icon: BarChart3, color: "blue" },
                { label: "Creators", value: analytics.users.creators, icon: Users, color: "purple" },
                { label: "Subscribed", value: analytics.users.subscribed_businesses, icon: CheckCircle, color: "green" }
              ].map((stat) => (
                <div key={stat.label} className="glass-card rounded-xl p-6 card-hover">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    stat.color === "cyan" ? "bg-cyan-500/20" :
                    stat.color === "blue" ? "bg-blue-500/20" : 
                    stat.color === "green" ? "bg-green-500/20" : "bg-purple-500/20"
                  }`}>
                    <stat.icon className={`w-6 h-6 ${
                      stat.color === "cyan" ? "text-cyan-400" :
                      stat.color === "blue" ? "text-blue-400" : 
                      stat.color === "green" ? "text-green-400" : "text-purple-400"
                    }`} />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Campaign & Revenue Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-lg font-bold font-['Syne'] text-white">Campaigns</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total</span>
                    <span className="font-semibold text-white">{analytics.campaigns.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Active</span>
                    <span className="font-semibold text-cyan-400">{analytics.campaigns.active}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Pending Moderation</span>
                    <span className="font-semibold text-yellow-400">{analytics.campaigns.pending_moderation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Participations</span>
                    <span className="font-semibold text-white">{analytics.campaigns.total_participations}</span>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <h3 className="text-lg font-bold font-['Syne'] text-white">Revenue</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Subscriptions</span>
                    <span className="font-semibold text-green-400">${analytics.revenue.subscription_revenue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Platform Fees</span>
                    <span className="font-semibold text-green-400">${analytics.revenue.platform_fees.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-4 border-t border-white/10">
                    <span className="text-white font-semibold">Total Revenue</span>
                    <span className="font-bold text-xl text-green-400">${analytics.revenue.total_revenue.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-bold font-['Syne'] text-white">Reward Pool</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Current Month</span>
                    <span className="font-semibold text-white">{analytics.reward_pool.current_month}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Pool Amount</span>
                    <span className="font-semibold text-purple-400">${analytics.reward_pool.pool_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status</span>
                    <span className={`font-semibold ${analytics.reward_pool.distributed ? 'text-green-400' : 'text-yellow-400'}`}>
                      {analytics.reward_pool.distributed ? "Distributed" : "Pending"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Payouts</span>
                    <span className="font-semibold text-white">${analytics.payouts.total_processed.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Moderation Tab */}
        {activeTab === "moderation" && (
          <div className="space-y-4">
            {pendingModeration.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <Shield className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">All Clear!</h3>
                <p className="text-gray-400">No campaigns pending review</p>
              </div>
            ) : (
              pendingModeration.map((campaign) => (
                <div key={campaign.campaign_id} className="glass-card rounded-xl p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white text-lg">{campaign.title}</h3>
                        <span className="px-3 py-1 rounded-full text-xs font-medium badge-warning">
                          Pending Review
                        </span>
                      </div>
                      <p className="text-gray-400 mb-3">{campaign.description}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {campaign.target_niches?.map((niche, i) => (
                          <span key={i} className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                            {niche}
                          </span>
                        ))}
                      </div>
                      <div className="text-sm text-gray-500">
                        Business: <span className="text-gray-300">{campaign.business_name}</span>
                        {campaign.budget_amount > 0 && (
                          <span className="ml-4 text-cyan-400">Budget: ${campaign.budget_amount}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleModerateCampaign(campaign.campaign_id, "rejected", "Does not meet guidelines")}
                        disabled={actionLoading === campaign.campaign_id}
                        className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleModerateCampaign(campaign.campaign_id, "approved")}
                        disabled={actionLoading === campaign.campaign_id}
                        className="px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-dark">
                <thead>
                  <tr>
                    <th className="px-6 py-4 text-left">User</th>
                    <th className="px-6 py-4 text-left">Email</th>
                    <th className="px-6 py-4 text-left">Type</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-left">Referral Code</th>
                    <th className="px-6 py-4 text-left">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id}>
                      <td className="px-6 py-4 text-white font-medium">{u.name}</td>
                      <td className="px-6 py-4 text-gray-400">{u.email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          u.user_type === "business" ? "bg-blue-500/20 text-blue-400" :
                          u.user_type === "creator" ? "bg-purple-500/20 text-purple-400" :
                          "bg-cyan-500/20 text-cyan-400"
                        }`}>
                          {u.user_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {u.user_type === "business" && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            u.is_subscribed ? "badge-success" : "bg-gray-500/20 text-gray-400"
                          }`}>
                            {u.is_subscribed ? "Subscribed" : "Free"}
                          </span>
                        )}
                        {u.user_type === "creator" && u.stripe_connect_account_id && (
                          <span className="badge-success px-3 py-1 rounded-full text-xs font-medium">
                            Payout Connected
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-cyan-400 font-mono text-sm">{u.referral_code}</td>
                      <td className="px-6 py-4 text-gray-400 text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === "campaigns" && (
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-dark">
                <thead>
                  <tr>
                    <th className="px-6 py-4 text-left">Campaign</th>
                    <th className="px-6 py-4 text-left">Business</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-left">Moderation</th>
                    <th className="px-6 py-4 text-left">Participants</th>
                    <th className="px-6 py-4 text-left">Budget</th>
                    <th className="px-6 py-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.campaign_id}>
                      <td className="px-6 py-4 text-white font-medium">{c.title}</td>
                      <td className="px-6 py-4 text-gray-400">{c.business_name}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          c.status === "active" ? "badge-success" :
                          c.status === "paused" ? "badge-warning" :
                          c.status === "pending_review" ? "badge-info" :
                          c.status === "completed" ? "badge-info" : "badge-purple"
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          c.moderation_status === "approved" ? "badge-success" :
                          c.moderation_status === "pending" ? "badge-warning" :
                          "bg-red-500/20 text-red-400"
                        }`}>
                          {c.moderation_status || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300">{c.total_participants}</td>
                      <td className="px-6 py-4 text-cyan-400">${c.budget_amount || 0}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {c.status === "active" && (
                            <button
                              onClick={() => handleUpdateCampaignStatus(c.campaign_id, "paused")}
                              disabled={actionLoading === c.campaign_id}
                              className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                              title="Pause"
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                          )}
                          {c.status === "paused" && (
                            <button
                              onClick={() => handleUpdateCampaignStatus(c.campaign_id, "active")}
                              disabled={actionLoading === c.campaign_id}
                              className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              title="Activate"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {(c.status === "active" || c.status === "paused") && (
                            <button
                              onClick={() => handleUpdateCampaignStatus(c.campaign_id, "cancelled")}
                              disabled={actionLoading === c.campaign_id}
                              className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Rewards Tab */}
        {activeTab === "rewards" && analytics && (
          <div className="space-y-6">
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold font-['Syne'] text-white mb-2">Monthly Reward Pool</h3>
                  <p className="text-gray-400">
                    10% of subscription revenue is distributed to active creators monthly
                  </p>
                </div>
                <button
                  onClick={handleDistributeRewards}
                  disabled={actionLoading === "distribute" || analytics.reward_pool.distributed}
                  className="btn-primary px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2 disabled:opacity-50"
                >
                  {actionLoading === "distribute" ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Award className="w-5 h-5" />
                  )}
                  Distribute Rewards
                </button>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Current Month</div>
                  <div className="text-2xl font-bold text-white">{analytics.reward_pool.current_month}</div>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Pool Amount</div>
                  <div className="text-2xl font-bold text-purple-400">${analytics.reward_pool.pool_amount.toFixed(2)}</div>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Distribution Status</div>
                  <div className={`text-2xl font-bold ${analytics.reward_pool.distributed ? 'text-green-400' : 'text-yellow-400'}`}>
                    {analytics.reward_pool.distributed ? "Complete" : "Pending"}
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Total Payouts</div>
                  <div className="text-2xl font-bold text-cyan-400">${analytics.payouts.total_processed.toFixed(2)}</div>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-bold font-['Syne'] text-white mb-4">Reward Distribution Rules</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>10% of each subscription ($9) goes to the monthly reward pool</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Pool is distributed based on creator performance (clicks, verified posts)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Campaign budgets: 80% to creators, 20% platform fee</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Business referral rewards: 1 free month for successful referrals</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
